import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Activity,
  CalendarClock,
  IndianRupee,
  Percent,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { KpiCard, KpiSkeleton } from "../../components/KpiCard";
import { PieCard } from "../../components/PieCard";
import { EmptyState } from "../../components/EmptyState";
import { LevelBadge } from "../../components/Badges";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { api, apiError } from "../../lib/api";
import { formatDate, formatINR, formatINRShort, formatPct, relativeTime } from "../../lib/format";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="font-medium">{label}</p>
      <p className="font-num mt-0.5 text-muted-foreground">{payload[0].value} clients</p>
    </div>
  );
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get("/api/admin/overview")
      .then(({ data: payload }) => {
        if (active) setData(payload);
      })
      .catch((error) => toast.error(apiError(error, "Could not load the overview")))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="page-loading-skeleton">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <KpiSkeleton count={5} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Overview"
        subtitle="Assets under management, client activity and pending operations."
        actions={
          <Button asChild data-testid="admin-overview-create-client">
            <Link to="/admin/clients/new">
              <UserPlus className="mr-2 h-4 w-4" /> New client
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard
          label="Total clients"
          value={data.totalClients}
          icon={Users}
          hint={`${data.activeClients} active \u00b7 ${data.suspendedClients} suspended`}
          testId="kpi-total-clients"
        />
        <KpiCard
          label="Total AUM"
          value={formatINR(data.totalAUM)}
          icon={IndianRupee}
          hint="Sum of client invested capital"
          testId="kpi-total-aum"
        />
        <KpiCard
          label="Profits today"
          value={formatINR(data.todayProfitsDistributed)}
          icon={TrendingUp}
          tone="profit"
          hint="Daily profit across all clients"
          testId="kpi-profits-today"
        />
        <KpiCard
          label="Avg overall return"
          value={formatPct(data.avgOverallProfitPercent)}
          icon={Percent}
          tone="profit"
          hint="Mean overall profit percentage"
          testId="kpi-avg-return"
        />
        <KpiCard
          label="Pending withdrawals"
          value={data.pendingWithdrawals}
          icon={CalendarClock}
          hint="Awaiting approval"
          testId="kpi-pending-withdrawals"
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PieCard
          title="Investments by type"
          subtitle="Total capital recorded per entry type"
          data={data.investmentsByType}
          testId="admin-investments-by-type-chart"
          emptyTitle="No investment entries yet"
          emptyDescription="Add investment entries to a client to see this breakdown."
        />
        <PieCard
          title="Withdrawals by status"
          subtitle="Amounts grouped by processing state"
          data={data.withdrawalsByStatus}
          testId="admin-withdrawals-by-status-chart"
          emptyTitle="No withdrawal entries yet"
          emptyDescription="Withdrawal records will be summarised here."
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Clients by level</CardTitle>
          </CardHeader>
          <CardContent>
            {data.clientsByLevel?.length ? (
              <div style={{ width: "100%", height: 220 }} data-testid="admin-clients-by-level-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.clientsByLevel} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      stroke="hsl(var(--border))"
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      stroke="hsl(var(--border))"
                      width={32}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--accent))" }} />
                    <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState
                icon={Users}
                title="No clients yet"
                description="Create your first client to populate this chart."
                className="border-0 bg-transparent py-8"
              />
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Upcoming withdrawal dates</CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcomingWithdrawals?.length ? (
              <ul className="divide-y divide-border">
                {data.upcomingWithdrawals.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-2.5">
                    <Link
                      to={`/admin/clients/${c.id}`}
                      className="min-w-0 text-sm font-medium hover:underline"
                    >
                      {c.name}
                    </Link>
                    <span className="font-num text-xs text-muted-foreground">
                      {formatDate(c.nextWithdrawalDate)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={CalendarClock}
                title="No upcoming dates"
                description="Set a next expected withdrawal date on a client profile."
                className="border-0 bg-transparent py-8"
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Recently added clients</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/clients">View all</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {data.recentClients?.length ? (
              <ul className="divide-y divide-border">
                {data.recentClients.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <Link
                        to={`/admin/clients/${c.id}`}
                        className="truncate text-sm font-medium hover:underline"
                      >
                        {c.name}
                      </Link>
                      <p className="truncate text-xs text-muted-foreground">{c.email}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <LevelBadge level={c.level} />
                      <span className="font-num text-xs">{formatINRShort(c.totalInvested)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={Users}
                title="No clients yet"
                description="Create the first investor account to get started."
                className="border-0 bg-transparent py-8"
                action={
                  <Button asChild size="sm">
                    <Link to="/admin/clients/new">Create client</Link>
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Recent admin activity</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/audit">Full log</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {data.recentActivity?.length ? (
              <ul className="divide-y divide-border">
                {data.recentActivity.map((log) => (
                  <li key={log.id} className="py-2.5">
                    <p className="text-sm">
                      <span className="font-medium">{log.action}</span>
                      <span className="text-muted-foreground"> — {log.details}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {log.adminName} · {relativeTime(log.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={Activity}
                title="No activity recorded"
                description="Administrative changes will be logged here automatically."
                className="border-0 bg-transparent py-8"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
